<template>
  <div class="full-monitor-grid">
    <div
      v-for="(camera, index) in cameras"
      :key="index"
      class="monitor-item"
      :style="{ animationDelay: `${index * 0.15}s` }"
    >
      <video
        v-if="camera.stream_url"
        :ref="el => setVideoRef(el, index)"
        autoplay
        muted
        loop
        playsinline
        class="monitor-video"
      ></video>
      <img
        v-else-if="camera.image_url"
        :src="camera.image_url"
        :alt="camera.name"
        class="monitor-image"
      />
      <div v-else class="nodata">无画面</div>
      <div class="monitor-info">
        <span class="monitor-name">{{ camera.name }}</span>
        <span :class="['status-dot', streamStatus[index] ? 'active' : 'offline']"></span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FullMonitorGrid',
  props: {
    cameras: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      streamStatus: {},
      videoRefs: {},
      connections: {},
      keepaliveTimers: {},     // 心跳保活定时器 (30s)
      reconnecting: {}         // 防重入标记
    }
  },
  watch: {
    cameras: {
      immediate: true,
      handler(newVal, oldVal) {
        this.stopAll()

        this.$nextTick(() => {
          newVal.forEach((camera, index) => {
            if (camera.stream_url) {
              this.connectWebRTC(camera, index)
            }
          })
        })
      }
    }
  },
  beforeDestroy() {
    this.stopAll()
  },
  methods: {
    setVideoRef(el, index) {
      if (el) {
        this.videoRefs[index] = el
      }
    },

    async connectWebRTC(camera, index) {
      const streamUrl = camera.stream_url
      if (!streamUrl) return

      try {
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        })

        pc.addTransceiver('video', { direction: 'recvonly' })
        pc.addTransceiver('audio', { direction: 'recvonly' })

        pc.ontrack = (event) => {
          this.streamStatus = { ...this.streamStatus, [index]: true }
          const video = this.videoRefs[index]
          if (video) {
            video.srcObject = event.streams[0]
          }
        }

        pc.oniceconnectionstatechange = () => {
          if (pc.iceConnectionState === 'disconnected') {
            this.streamStatus = { ...this.streamStatus, [index]: false }
          }
          if (pc.iceConnectionState === 'failed') {
            this.streamStatus = { ...this.streamStatus, [index]: false }
          }
        }

        this.connections[index] = pc

        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)

        const isWhep = streamUrl.includes('whep')

        if (isWhep) {
          // MediaMTX WHEP
          const res = await fetch(streamUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/sdp' },
            body: pc.localDescription.sdp
          })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const sdp = await res.text()
          await pc.setRemoteDescription({ type: 'answer', sdp })
        } else if (streamUrl.includes('webrtc')) {
          // go2rtc WebRTC
          await new Promise((resolve) => {
            if (pc.iceGatheringState === 'complete') {
              resolve()
            } else {
              pc.onicegatheringstatechange = () => {
                if (pc.iceGatheringState === 'complete') resolve()
              }
            }
          })

          const res = await fetch(streamUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/sdp' },
            body: pc.localDescription.sdp
          })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const sdp = await res.text()
          await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp }))
        } else {
          throw new Error('无法识别流协议，URL 需包含 whep 或 webrtc')
        }

        // 连接成功后启动 30s 心跳保活
        this.startKeepalive(camera, index)
      } catch (err) {
        console.error(`${camera.name} 连接失败:`, err.message)
        this.streamStatus = { ...this.streamStatus, [index]: false }
      }
    },

    // 启动心跳保活定时器（30s 间隔）
    startKeepalive(camera, index) {
      this.clearKeepalive(index)
      this.keepaliveTimers[index] = setInterval(() => {
        this.reconnectStream(camera, index)
      }, 30000)
    },

    // 清除指定摄像头的保活定时器
    clearKeepalive(index) {
      if (this.keepaliveTimers[index]) {
        clearInterval(this.keepaliveTimers[index])
        delete this.keepaliveTimers[index]
      }
    },

    // 无缝重连：后台建新连接，收到流后再切换，避免黑屏
    async reconnectStream(camera, index) {
      // 防止上一次重连还未完成
      if (this.reconnecting[index]) return
      this.reconnecting = { ...this.reconnecting, [index]: true }

      const oldPc = this.connections[index]
      const streamUrl = camera.stream_url
      if (!streamUrl) {
        this.reconnecting = { ...this.reconnecting, [index]: false }
        return
      }

      try {
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        })

        pc.addTransceiver('video', { direction: 'recvonly' })
        pc.addTransceiver('audio', { direction: 'recvonly' })

        let newStream = null

        pc.ontrack = (event) => {
          newStream = event.streams[0]
        }

        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)

        const isWhep = streamUrl.includes('whep')

        if (isWhep) {
          const res = await fetch(streamUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/sdp' },
            body: pc.localDescription.sdp
          })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const sdp = await res.text()
          await pc.setRemoteDescription({ type: 'answer', sdp })
        } else if (streamUrl.includes('webrtc')) {
          await new Promise((resolve) => {
            if (pc.iceGatheringState === 'complete') {
              resolve()
            } else {
              pc.onicegatheringstatechange = () => {
                if (pc.iceGatheringState === 'complete') resolve()
              }
            }
          })

          const res = await fetch(streamUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/sdp' },
            body: pc.localDescription.sdp
          })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const sdp = await res.text()
          await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp }))
        } else {
          throw new Error('无法识别流协议')
        }

        // 新连接就绪，无缝切换
        if (newStream) {
          const video = this.videoRefs[index]
          if (video) {
            video.srcObject = newStream
          }
        }

        // 切换完成后关闭旧连接
        if (oldPc) {
          oldPc.close()
        }
        this.connections = { ...this.connections, [index]: pc }
        this.streamStatus = { ...this.streamStatus, [index]: true }

        console.log(`${camera.name} 心跳保活完成`)
      } catch (err) {
        console.warn(`${camera.name} 心跳保活失败:`, err.message)
        // 保活失败不影响当前正在播放的旧连接
      } finally {
        this.reconnecting = { ...this.reconnecting, [index]: false }
      }
    },

    stopStream(index) {
      this.clearKeepalive(index)
      this.reconnecting = { ...this.reconnecting, [index]: false }
      const pc = this.connections[index]
      if (pc) {
        pc.close()
        delete this.connections[index]
      }
      const video = this.videoRefs[index]
      if (video) {
        video.srcObject = null
      }
      this.streamStatus = { ...this.streamStatus, [index]: false }
    },

    stopAll() {
      Object.keys(this.keepaliveTimers).forEach(index => {
        this.clearKeepalive(parseInt(index))
      })
      Object.keys(this.connections).forEach(index => {
        this.stopStream(parseInt(index))
      })
      this.connections = {}
      this.reconnecting = {}
    }
  }
}
</script>

<style scoped>
.full-monitor-grid {
  width: 100%;
  height: 90%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 4px;
  padding: 4px;
}

.monitor-item {
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  background: rgba(49, 130, 206, 0.2);
  animation: fadeIn 0.5s ease-out forwards;
  opacity: 0;
}

.monitor-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.monitor-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.nodata {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
}

.monitor-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
}

.monitor-name {
  font-size: 14px;
  color: #e2e8f0;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #48bb78;
}

.status-dot.active {
  animation: pulse 2s infinite;
}

.status-dot.offline {
  background: #e94560;
  animation: none;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>